import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, saveTokens } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, ShieldCheck, Building2, UserPlus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const INST_LABEL = {
  universidad: "Universidad", hospital: "Hospital", clinica: "Clínica",
  entidad_social: "Entidad social", investigacion: "Investigación",
  particular: "Particular", otro: "Otro",
};
const ROLE_LABEL = { patient: "Paciente", professional: "Profesional", researcher: "Investigador" };

export default function InviteRegister() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "", purpose: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => setInv(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Invitación no disponible"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/invitations/${token}/register`, form);
      if (data.auto_approved) {
        saveTokens(data.access_token, data.refresh_token);
        setUser(data.user);
        toast.success("Cuenta creada y aprobada");
        const target = data.user.role === "patient" ? "/dashboard" : data.user.role === "admin" ? "/admin/users" : "/pro";
        nav(target);
      } else {
        setDone(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar el registro");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <Card className="max-w-md w-full rounded-2xl border-border" data-testid="invite-error">
          <CardHeader>
            <CardTitle className="heading">Invitación no disponible</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/" className="w-full"><Button variant="outline" className="w-full rounded-full">Volver al inicio</Button></Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <Card className="max-w-lg w-full rounded-2xl border-border" data-testid="invite-pending">
          <CardHeader className="text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/15 text-primary grid place-items-center mb-2"><ShieldCheck size={26} /></div>
            <CardTitle className="heading">Solicitud enviada</CardTitle>
            <CardDescription>{done.message}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button className="w-full rounded-full h-11" onClick={() => nav("/login")}>Ir a iniciar sesión</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!inv) return <div className="p-8">Verificando invitación...</div>;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <div className="bg-primary-foreground rounded-2xl px-4 py-3 inline-flex self-start">
          <Logo size="md" testid="invite-brand-logo" />
        </div>
        <div>
          <p className="text-primary-foreground/85 uppercase tracking-widest text-sm">Invitación aceptada</p>
          <h1 className="heading text-4xl font-extrabold mt-2 leading-tight">
            Estás siendo invitado a<br />{inv.institution}
          </h1>
          <p className="mt-4 text-primary-foreground/85 max-w-md text-lg">
            Completa tus datos y crea tu contraseña. {inv.auto_approve ? "Tu acceso queda activado automáticamente." : "Tu solicitud pasará por revisión antes de activarse."}
          </p>
          {inv.note && <p className="mt-4 italic text-primary-foreground/70 border-l-2 border-primary-foreground/40 pl-3">"{inv.note}"</p>}
        </div>
        <div className="text-sm opacity-75">© 2026 SocialCog Training®</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <Card className="w-full max-w-md border-border rounded-2xl">
          <CardHeader>
            <CardTitle className="heading text-2xl">Crear cuenta invitada</CardTitle>
            <CardDescription>
              Se te asignará automáticamente la entidad y el rol elegido por el administrador.
            </CardDescription>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className="bg-primary/15 text-primary" data-testid="invite-role-badge"><UserPlus size={11} className="mr-1" /> {ROLE_LABEL[inv.allowed_role]}</Badge>
              <Badge variant="outline" data-testid="invite-institution-badge"><Building2 size={11} className="mr-1" /> {inv.institution} · {INST_LABEL[inv.institution_type]}</Badge>
              {inv.auto_approve && <Badge className="bg-success/20 text-success"><ShieldCheck size={11} className="mr-1" /> Aprobación automática</Badge>}
            </div>
          </CardHeader>
          <form onSubmit={submit}>
            <CardContent className="space-y-4">
              <div>
                <Label>Nombre completo</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" data-testid="inv-name-input" />
              </div>
              <div>
                <Label>Correo</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11" data-testid="inv-email-input" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 h-11" data-testid="inv-password-input" />
              </div>
              <div>
                <Label>País (opcional)</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-2 h-11" data-testid="inv-country-input" />
              </div>
              <div>
                <Label>Motivo de uso (opcional)</Label>
                <Textarea rows={2} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="mt-2" data-testid="inv-purpose-input" />
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={loading} className="w-full h-12 text-base rounded-full" data-testid="inv-submit-btn">
                {loading ? "Enviando..." : (inv.auto_approve ? "Crear cuenta y entrar" : "Enviar solicitud")}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
